import Close from "@mui/icons-material/Close";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  FormControlProps,
  FormHelperText,
  InputAdornment,
  InputBase,
  InputBaseProps,
  InputLabel,
} from "@mui/material";
import {
  Controller,
  FieldErrors,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import { useRef, useEffect } from "react";
import { CustomStyles, getStyles } from "src/styles/theme";
import defaultStyles, { StylesClasses } from "./styles";

type InputProps<T> = UseControllerProps<T> &
  FormControlProps &
  InputBaseProps & {
    label?: string;
    errors?: FieldErrors;
    maxLength?: number;
    labelPos?: boolean;
    labelPosStyles?: boolean;
    readOnly?: boolean;
    showAdornment?: boolean;
    adornmentPos?: "start" | "end";
    iconSrc?: string;
    placeholder?: string;
    autoFocus?: boolean;
    isValid?: boolean;
    isAlpha?: boolean;
    height?: string;
    allowFloatValue?: boolean;
    helperText?: string;
    validationTrigger?: any;
    customStyles?: CustomStyles<StylesClasses>;
  };

export const Input = <T extends FieldValues>({
  name,
  control,
  label,
  type = "text",
  fullWidth = true,
  labelPos = false,
  labelPosStyles = false,
  disabled = false,
  rules,
  maxLength,
  inputProps,
  placeholder,
  isAlpha = false,
  showAdornment = false,
  adornmentPos = "end",
  variant = "standard",
  className,
  isValid = false,
  readOnly = false,
  autoFocus = false,
  height = "40px",
  allowFloatValue = true,
  helperText,
  validationTrigger,
  customStyles,
  ...rest
}: InputProps<T>) => {
  const styles = getStyles<StylesClasses>(defaultStyles, customStyles);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  const handleAutoGrow = () => {
    if (inputRef.current && rest.multiline) {
      const textarea = inputRef.current as HTMLTextAreaElement;
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 600) + "px";
    }
  };

  const handleKeyDown = (event) => {
    if (
      type === "number" &&
      !allowFloatValue &&
      event.keyCode !== 8 &&
      (event.key === "e" ||
        event.key === "E" ||
        event.key === "." ||
        !/^\d$/.test(event.key))
    ) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    handleAutoGrow();
  }, [rest.multiline]);

  return (
    <Controller
      render={({ field, fieldState }) => {
        // Wrap onChange to trigger real-time validation if rules exist
        const handleChange = (e) => {
          field.onChange(e);
          handleAutoGrow();
          // Trigger validation on change if rules exist and trigger is provided
          if (rules && validationTrigger) {
            validationTrigger(name);
          }
        };

        return (
          <Box
            {...styles("wrapper", {
              color: readOnly ? "custom.text.secondary" : "initial",
            })}
          >
            {label && (
              <InputLabel
                shrink
                {...styles("label", {
                  color: readOnly ? "custom.text.secondary" : "initial",
                })}
                required={!!rules?.required}
                htmlFor={name}
              >
                {label}
              </InputLabel>
            )}
            <InputBase
              id={name}
              {...styles("input", {
                color: readOnly ? "custom.text.secondary" : "initial",
                height: rest.multiline ? "auto" : { height },
              })}
              type={type}
              value={field?.value}
              disabled={disabled}
              onChange={handleChange}
              inputRef={(ref) => {
                field.ref(ref);
                inputRef.current = ref;
              }}
              onBlur={field.onBlur}
              readOnly={readOnly}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus={autoFocus}
              inputProps={{
                maxLength: maxLength,
                autoComplete: "off",
                style: rest.multiline
                  ? { overflow: "hidden", resize: "none" }
                  : {},
                ...inputProps,
              }}
              endAdornment={
                <InputAdornment
                  aria-label="Search Icon"
                  {...styles("adornment", {
                    display:
                      showAdornment && adornmentPos === "start"
                        ? "block"
                        : "none",
                  })}
                  position="start"
                >
                  <SearchIcon />
                </InputAdornment>
              }
              startAdornment={
                <InputAdornment
                  {...styles("adornment", {
                    display:
                      showAdornment && adornmentPos === "end"
                        ? "block"
                        : "none",
                  })}
                  position="end"
                >
                  {!!fieldState.error ? (
                    <Close color="error" />
                  ) : (
                    field.value !== "" && (
                      <DoneRoundedIcon {...styles("doneIcon")} />
                    )
                  )}
                </InputAdornment>
              }
              error={!!fieldState.error}
              {...rest}
            />
            {fieldState.error && (
              <FormHelperText {...styles("errorMsg")}>
                {fieldState.error?.message}
              </FormHelperText>
            )}
            {!fieldState.error && helperText && (
              <FormHelperText {...styles("errorMsg")} sx={{ color: "black" }}>
                {helperText}
              </FormHelperText>
            )}
          </Box>
        );
      }}
      name={name}
      control={control}
      rules={rules}
      {...rest}
    />
  );
};
